// Sugalarm — cycle 404: "which of the household's heroes am I?"
//
// The press-3 opt-out lands on the alert row as a list of CONTACT ids, and
// the SPA knows the signed-in ACCOUNT. The join between the two already
// exists — cycle 403's `emergency_contacts.linked_user_id`, the same field
// that makes `call_quiet` possible — so this hook is nothing but that
// lookup, done at the one moment it is needed.
//
// ⚠ NO NEW ENDPOINT AND NO NEW WS FRAME. A per-account "who am I on the
// hero list" field on the alert payload would have been a broadcast of one
// household member's identity to every device on the member audience; the
// account asking about ITSELF, over a list it is already allowed to read,
// is the smaller thing.
//
// ⚠ LAZY, AND ONE FETCH PER EPISODE. `enabled` is false on every screen
// that is not a live urgent low, so a household that never has an alarm
// never makes this call at all. During an alarm the consuming surface
// re-renders roughly once a second (the countdown), so the latch is what
// keeps a lookup from becoming a poll — it re-arms only when `enabled`
// falls, which is the episode ending.
//
// ⚠ IT FAILS OPEN TOWARD NULL — i.e. toward NOT offering the action. A
// refusal, a network blip, an old server without the field, or a contact
// list this account is simply not on all resolve the same way: no id, no
// affordance, nothing lost from the alarm surface.

import { useEffect, useRef, useState } from "react";

import * as emergencyContactsApi from "../api/emergencyContacts";

/**
 * @param enabled  Gate the lookup. The caller is expected to fold in
 *                 "the episode is a live urgent low" AND "this is not the
 *                 kiosk" — the kiosk authenticates as the household owner,
 *                 so an id WOULD resolve there and it must never be asked
 *                 for. See `optOutUndoVisible`'s kiosk guard.
 * @param userId   The signed-in account id (`auth.user.id`), or null.
 */
export function useMyLinkedContactId(
  enabled: boolean,
  userId: string | null,
): string | null {
  const [contactId, setContactId] = useState<string | null>(null);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !userId) {
      // Re-arm for the next episode: the household may have linked or
      // unlinked a hero since the last alarm, and the cache is only ever
      // meant to span one.
      loadedRef.current = false;
      setContactId(null);
      return;
    }
    if (loadedRef.current) return;
    loadedRef.current = true;
    let cancelled = false;
    void (async () => {
      try {
        const r = await emergencyContactsApi.list();
        if (cancelled || !r.ok) return;
        const mine = r.data.contacts.find((c) => c.linked_user_id === userId);
        setContactId(mine?.id ?? null);
      } catch {
        // Stay null — indistinguishable from "this account is not a hero".
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, userId]);

  return contactId;
}
